import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatParty, isLocationType } from '@/venue/venue';
import type { MenuItem } from './api';
import { Amount, Notice, Panel } from './parts';
import { ItemSheet } from './sheets';
import { useActiveSessions, useMenu, useSessionDetail, useStaffLive } from './queries';
import { describeStaffError, locationText } from './wording';
import { PageHeader } from '@/components/PageHeader';
import { SectionHeading } from '@/components/SectionHeading';

function SessionPicker() {
  const sessions = useActiveSessions();
  return (
    <section aria-labelledby="menu-title" className="flex flex-col gap-4">
      <PageHeader id="menu-title" title="Menu" subtitle="Choose whose cart to add to." />
      {sessions.isPending && (
        <LoadingRegion slowHint label="Loading sessions">
          <Skeleton className="h-16" />
        </LoadingRegion>
      )}
      {sessions.isError && !sessions.data && (
        <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
      )}
      {sessions.data && sessions.data.length === 0 && (
        <EmptyState
          icon="floor"
          title="No active sessions"
          message="Open a session from the floor first."
        />
      )}
      {sessions.data && sessions.data.length > 0 && (
        <Panel className="md:max-w-xl">
          <ul className="divide-y divide-line">
            {sessions.data.map((s) => (
              <li key={s.session_id}>
                <Link
                  to={`/staff/menu?session=${s.session_id}`}
                  className="flex min-h-14 items-center gap-3 px-4 py-2 text-ink no-underline hover:bg-surface"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-base font-semibold">
                      {locationText(s.location.location_type, s.location.label)}
                    </span>
                    <span className="text-sm text-muted">
                      {isLocationType(s.location.location_type)
                        ? formatParty(s.location.location_type, s.party_display_name, s.guest_count)
                        : s.party_display_name}
                    </span>
                  </span>
                  <Icon name="chevronRight" size={18} className="shrink-0 text-faint" />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </section>
  );
}

function MenuForSession({ sessionId }: { sessionId: string }) {
  const detail = useSessionDetail(sessionId);
  const menu = useMenu();
  const [selected, setSelected] = useState<MenuItem | null>(null);

  const session = detail.data;
  const type = session?.location.location_type ?? 'TABLE';
  const where = session ? locationText(type, session.location.label) : '';
  const closed = session?.status === 'CLOSED';
  // "Table 3 (Priyanka …)": the place first, because that is how a waiter finds the party.
  const who = session ? `${where} (${session.party_display_name})` : 'the customer';

  return (
    <section aria-labelledby="menu-title" className="flex flex-col gap-4">
      <PageHeader
        id="menu-title"
        title="Add to cart"
        subtitle={
          session ? (
            <>
              Adding to <strong className="text-ink">{who}</strong>
              {' · '}
              <Link
                to={`/staff/sessions/${sessionId}`}
                className="-my-2 inline-flex min-h-11 items-center font-semibold text-primary"
              >
                View cart
              </Link>
            </>
          ) : (
            <Link to="/staff/menu" className="text-sm font-semibold text-primary">
              Choose a different session
            </Link>
          )
        }
      />

      {detail.isError && !detail.data && (
        <Notice tone="error">{describeStaffError(detail.error, 'load')}</Notice>
      )}
      {closed && <Notice tone="warn">This session is closed. Nothing more can be added.</Notice>}

      {menu.isPending && (
        <LoadingRegion slowHint label="Loading the menu">
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </LoadingRegion>
      )}
      {menu.isError && !menu.data && (
        <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />
      )}
      {menu.isError && menu.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void menu.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the menu. Showing the last menu received.
        </Notice>
      )}
      {menu.data && menu.data.categories.every((c) => c.items.length === 0) && (
        <EmptyState
          icon="menu"
          title="The menu is empty"
          message="Nothing can be ordered right now."
        />
      )}

      {/* Categories stack full width; inside each, items share two columns from tablet width. No masonry. */}
      <div className="flex flex-col gap-4">
        {(menu.data?.categories ?? [])
          .filter((category) => category.items.length > 0)
          .map((category) => (
            <section
              key={category.id}
              aria-labelledby={`cat-${category.id}`}
              className="flex flex-col gap-2"
            >
              <SectionHeading id={`cat-${category.id}`} count={category.items.length}>
                {category.name}
              </SectionHeading>
              <Panel className="overflow-hidden">
                <ul className="grid gap-px bg-line md:grid-cols-2">
                  {category.items.map((item) => {
                    const blocked = !item.is_available || !session || closed;
                    return (
                      <li key={item.id} className="bg-white">
                        <button
                          type="button"
                          disabled={blocked}
                          onClick={() => setSelected(item)}
                          className={cn(
                            'flex min-h-14 w-full items-center justify-between gap-3 px-4 py-2 text-left',
                            blocked ? 'cursor-not-allowed text-faint' : 'text-ink hover:bg-surface',
                          )}
                        >
                          <span className="min-w-0">
                            <span className="line-clamp-2 block text-base font-semibold break-words">
                              {item.name}
                            </span>
                            {item.description && (
                              <span className="line-clamp-1 block text-sm break-words text-muted">
                                {item.description}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 flex-col items-end gap-1">
                            <Amount
                              minor={item.base_price}
                              className="text-sm font-bold tabular-nums"
                            />
                            {!item.is_available && <Badge tone="neutral">Unavailable</Badge>}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Panel>
            </section>
          ))}
      </div>

      {/* Keyed by item so each opening starts with fresh choices and a fresh idempotency key. */}
      {selected && session && (
        <ItemSheet
          key={selected.id}
          item={selected}
          sessionId={sessionId}
          cartRevision={session.draft_cart.revision}
          who={who}
          reviewHref={`/staff/sessions/${sessionId}`}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

export function MenuPage() {
  useStaffLive();
  const [params] = useSearchParams();
  const sessionId = params.get('session');
  return sessionId ? <MenuForSession sessionId={sessionId} /> : <SessionPicker />;
}
